import { Box, Typography } from '@mui/material';
import { useAddressForm, useDobForm, usePhoneForm, useNameForm, usePronounsForm, useSexForm, NameForm, DobForm, SexForm, PronounsForm, AddressForm, PhoneForm, EmergencyContactForm } from '../forms';
import { Alert, CheckYourInboxDialog, EditableSectionContainer, EditableSectionRow, formatAddress, LinkButton, LoginSettingsSection, useEditableSectionsContext, VerificationInfoLabel } from '@uplift/ui-v2';
import { trackToDoCTA, useIsMobile, useL10n } from '@uplift/hooks';
import { ContentStack, EmptyEmergencyContact } from '../components';
import { useSettingsStyles } from '../styles';
import React, { useCallback, useMemo } from 'react';
import { useEmergencyContactForm } from '../forms/emergency-contact-form/use-emergency-contact-form.hook';
import { useStateQuery, useUserContactsQuery, useUserEmergencyContactQuery, useUserWithProfileQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { ActionItemKeys, ContactName, requestNewVerifyEmail } from '@uplift/api';
import { UserRole } from '@uplift/types';

export type SectionType = 'name' | 'dob' | 'sex' | 'pronouns' | 'address' | 'phone' | 'emergencyContact';

const enum ProfileRow {
  name = 'name',
  dob = 'dob',
  sex = 'sex',
  pronouns = 'pronouns',
  address = 'address',
  phone = 'phone',
  email = 'email',
  emergencyContact = 'emergencyContact',
}

export const ProfileSettings: React.VFC = () => {
  const me = useCurrentuser();
  const { data: { user, profile } } = useUserWithProfileQuery(me.id, { suspense: true });
  const { data: profileState } = useStateQuery(profile.address.stateId, { suspense: true });
  const { data: emergencyContact } = useUserEmergencyContactQuery(me.id, { suspense: true });
  const { data: contacts } = useUserContactsQuery(me.id, { suspense: true });
  const l10n = useL10n();
  const { classes } = useSettingsStyles();
  const isSmDown = useIsMobile('sm');
  const isSmUp = !isSmDown;
  const { nameFormik } = useNameForm();
  const { dobFormik } = useDobForm();
  const { sexFormik } = useSexForm();
  const { pronounsFormik } = usePronounsForm();
  const { addressFormik } = useAddressForm();
  const { phoneFormik } = usePhoneForm();
  const { emergencyContactFormik } = useEmergencyContactForm();
  const [ emailVerificationModalOpen, setEmailVerificationModalOpen ] = React.useState(false);

  const handleEmailVerificationOpen = useCallback(async () => {
    await requestNewVerifyEmail();
    trackToDoCTA({
      toDoType: ActionItemKeys.CLIENT_EMAIL_VERIFY,
      userType: UserRole.patient
    });

    setEmailVerificationModalOpen(true);
  }, []);

  const handleEmailVerificationClose = () => {
    setEmailVerificationModalOpen(false);
  };

  const verifyEmail = isSmDown ? handleEmailVerificationOpen : null;

  const addressFormatted = formatAddress({
    address1: profile?.address.line1,
    address2: profile?.address.line2,
    city: profile?.address.city,
    zip: profile?.address.zip,
  }, profileState);


  const { preferredName, dob, sex, pronouns } = profile;
  const homeContact = contacts.find(c => c.name === ContactName.home);
  const mobileContact = contacts.find(c => c.name === ContactName.mobile);
  const legalName = [ user.firstName, user.middleName, user.lastName ].filter(x => x).join(' ');
  const name = preferredName || user.firstName;

  const emergencyContactContent = useMemo(() => {
    if (emergencyContact?.name) {
      return (
        <Box>
          {isSmDown ? (
            <Box display="flex" flexDirection="column">
              <ContactNumber label="" value={emergencyContact?.name} />
            </Box>
          ) : (
            <Box>
              <Box flex={1}>
                <ContentStack title={`(${emergencyContact?.relationship})`} value={emergencyContact?.name} titlePlacement="end"/>
              </Box>
              <Box flex={1}>
                <ContentStack title={l10n.accountSettings.profile.phone} value={emergencyContact?.phone || undefined} titlePlacement="end" />
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return <EmptyEmergencyContact />;
  }, [ emergencyContact, isSmDown ]);

  const [ scrolled, setScrolled ] = React.useState(false);
  const { editKey } = useEditableSectionsContext();

  React.useEffect(() => {
    if (
      !editKey
      || scrolled
    ) return;

    setTimeout(() => {
      const element = document.getElementById(editKey);

      if (!element) return;

      element.scrollIntoView({ block: 'center' });
      setScrolled(true);
    }, 10);
  }, [ editKey ]);

  return (
    <Box display="flex" flexDirection="column" gap="1.5rem">
      <EditableSectionContainer
        title={l10n.accountSettings.profile.aboutYou}
        rowDividers
        formikConfigs={{
          [ProfileRow.name]: nameFormik,
          [ProfileRow.dob]: dobFormik,
          [ProfileRow.sex]: sexFormik,
          [ProfileRow.pronouns]: pronounsFormik
        }}
      >

        <EditableSectionRow
          rowKey={ProfileRow.name}
          label={l10n.accountSettings.profile.name}
          form={<NameForm />}
          kind="editable-row"
        >
          {isSmUp ? (
            <Box display="flex">
              <Box flex={1}>
                <ContentStack title={l10n.accountSettings.profile.legal} value={legalName} />
              </Box>
              <Box flex={1}>
                <ContentStack title={l10n.accountSettings.profile.preferred} value={preferredName || undefined} />
              </Box>
            </Box>
          ) : (
            <ContentStack value={name} />
          )}
        </EditableSectionRow>

        <EditableSectionRow
          rowKey={ProfileRow.dob}
          label={l10n.accountSettings.dateOfBirth}
          form={<DobForm />}
          kind="editable-row"
        >
          <ContentStack value={dob} />
        </EditableSectionRow>

        <EditableSectionRow
          rowKey={ProfileRow.sex}
          label={l10n.accountSettings.profile.sex}
          form={<SexForm />}
          kind="editable-row"
        >
          <ContentStack value={sex} valueClassName={classes.capitalize} />
        </EditableSectionRow>

        <EditableSectionRow
          rowKey={ProfileRow.pronouns}
          label={l10n.accountSettings.profile.pronouns}
          form={<PronounsForm />}
          kind="editable-row"
        >
          <ContentStack value={pronouns || undefined} />
        </EditableSectionRow>

      </EditableSectionContainer>

      <EditableSectionContainer
        title={l10n.accountSettings.profile.contactInformation}
        rowDividers
        formikConfigs={{
          [ProfileRow.address]: addressFormik,
          [ProfileRow.phone]: phoneFormik,
          [ProfileRow.emergencyContact]: emergencyContactFormik,
        }}
      >
        <EditableSectionRow
          rowKey={ProfileRow.address}
          label={l10n.accountSettings.profile.homeAddress}
          form={<AddressForm />}
          kind="editable-row"
        >
          {isSmUp ? (
            <ContentStack value={addressFormatted.address}>
              <Box mt={1} />
              <Typography variant="body1">{addressFormatted.addressCityState}</Typography>
            </ContentStack>
          ) : (
            <ContentStack value={addressFormatted.fullAddress} ellipseValueOnMobile />
          )}
        </EditableSectionRow>

        <EditableSectionRow
          rowKey={ProfileRow.phone}
          label={l10n.accountSettings.profile.phone}
          form={<PhoneForm />}
          kind="editable-row"
        >
          <Box className={classes.phoneBlock}>
            <Box flex={1}>
              <ContentStack title={l10n.accountSettings.profile.mobile} value={mobileContact?.value}/>
            </Box>
            <Box flex={1}>
              <ContentStack title={l10n.accountSettings.profile.home} value={homeContact?.value || undefined}/>
            </Box>
          </Box>
        </EditableSectionRow>

        <EditableSectionRow
          rowKey={ProfileRow.emergencyContact}
          label={l10n.accountSettings.profile.emergencyContact}
          editButtonText={emergencyContact?.name ? l10n.general.edit : l10n.general.add}
          form={<EmergencyContactForm />}
          kind="editable-row"
          // hideLabel={isMobile || !emergencyContact.name}
          rowFormWrapperProps={{ mobileModalTitle: 'Edit Emergency Contact' }}
        >
          {emergencyContactContent}
        </EditableSectionRow>

        <EditableSectionRow
          rowKey={ProfileRow.email}
          label={l10n.accountSettings.profile.emailAddress}
          kind="custom"
        >
          <Box onClick={verifyEmail}>
            <VerificationInfoLabel label={user?.email} verified={user?.emailVerified} />
            <Box mt={{ xs: 0.5, sm: 0 }} />
            {!user?.emailVerified && (
              <Box className={classes.alert}>
                <Alert severity="warning">Unverified—add extra security and <LinkButton variant="body2" color="textPrimary" onClick={handleEmailVerificationOpen}>verify your email</LinkButton></Alert>
              </Box>
            )}
            <Typography variant="body2">{l10n.accountSettings.profile.changeEmail}</Typography>
          </Box>
        </EditableSectionRow>

      </EditableSectionContainer>

      <LoginSettingsSection userId={user.id} />
      <CheckYourInboxDialog open={emailVerificationModalOpen} onClose={handleEmailVerificationClose}/>
    </Box>
  );
};

type ContactNumberProps = {
  label: string;
  value?: string;
};

const ContactNumber: React.FC<ContactNumberProps> = ({ label, value }) => {
  const { classes } = useSettingsStyles();

  if (!value) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap="4px">
      <Box minWidth="130px">
        <Typography className={classes.semiBold}>
          {value}
        </Typography>
      </Box>
      <Typography color="textSecondary">
        {label}
      </Typography>
    </Box>
  );
};
